import type {
  NestingConfirm,
  PlanRowCreate,
  ProductionPriorityInput,
  StageOutput,
  StagePlan,
  StageReference,
} from '@veem/crm-schema'
import { showToast } from '@veem/ui'
import { createSignal } from 'solid-js'
import { ApiError, api, reportRefreshFailure } from '../../lib/api.ts'
import { session } from '../../lib/session.ts'
import { hydrateData } from '../../prototype/data/hydration.ts'
import { type ProtoState, proto } from '../../prototype/state/store.ts'

type Versioned = { id: string; version: number }
const versionOf = (value: unknown) => {
  const record = value as Partial<Versioned> | undefined
  if (!record?.version) throw new Error('Оновіть сторінку перед зміною даних')
  return record.version
}
const production = (reference: string) => {
  const customer = proto.state.orders.find(
    (order) =>
      order.deal.id === reference ||
      (order.production && `zm-${order.production.number.replace(/\D/g, '')}` === reference),
  )
  const order =
    customer?.production ?? proto.state.internalOrders.find((order) => order.id === reference)
  if (!order) throw new Error('Виробниче замовлення не знайдено')
  return order as typeof order & Versioned
}
const stageReference = (id: string, key: string): StageReference => {
  const order = production(id)
  const stage = order.stages.find((row) => row.key === key)
  return { productionOrderId: order.id, stageKey: key, version: versionOf(stage) }
}
export const [productionError, setProductionError] = createSignal('')
const pending = new Set<string>()
const [pendingCount, setPendingCount] = createSignal(0)
export const productionIsPending = (key: string) => {
  pendingCount()
  return pending.has(key)
}
const refresh = async (actorId: string | undefined) => {
  try {
    const snapshot = await api<{ data: Record<string, unknown>; state: Partial<ProtoState> }>(
      '/snapshot',
    )
    if (session()?.actor.id === actorId) {
      hydrateData(snapshot.data)
      proto.hydrate(snapshot.state)
    }
  } catch {
    reportRefreshFailure()
  }
}

export const productionWrite = async (
  key: string,
  path: string,
  input: () => unknown,
  method = 'POST',
) => {
  if (pending.has(key)) return false
  pending.add(key)
  setPendingCount(pending.size)
  setProductionError('')
  const actorId = session()?.actor.id
  try {
    await api(path, { method, body: JSON.stringify(input()) })
    await refresh(actorId)
    return true
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Не вдалося зберегти зміни'
    setProductionError(message)
    if (error instanceof ApiError && error.status === 409) {
      try {
        const snapshot = await api<{ data: Record<string, unknown>; state: Partial<ProtoState> }>(
          '/snapshot',
        )
        if (session()?.actor.id === actorId) {
          hydrateData(snapshot.data)
          proto.hydrate(snapshot.state)
        }
      } catch {
        // Keep the original conflict visible if its follow-up refresh is unavailable.
      }
    }
    showToast(message, 'error')
    return false
  } finally {
    pending.delete(key)
    setPendingCount(pending.size)
  }
}
/**
 * Adds a compressed completion photo to the stage history. Throws the server's message, so the
 * finish dialog can keep the photo and offer a retry or finishing without it.
 */
export const uploadStagePhoto = async (id: string, key: string, photo: File) => {
  const body = new FormData()
  body.set('file', photo, photo.name)
  const actorId = session()?.actor.id
  let response: Response
  try {
    response = await fetch(
      `/api/production/${encodeURIComponent(production(id).id)}/stages/${encodeURIComponent(key)}/photo`,
      { method: 'POST', body, credentials: 'include' },
    )
  } catch {
    throw new ApiError('Немає зв’язку із сервером. Спробуйте ще раз.', 0, 'NETWORK_ERROR')
  }
  const result = (await response.json().catch(() => null)) as {
    ok?: boolean
    error?: { message?: string; code?: string }
  } | null
  if (!response.ok || !result?.ok)
    throw new ApiError(
      result?.error?.message ?? 'Не вдалося завантажити фото',
      response.status,
      result?.error?.code ?? 'UPLOAD_ERROR',
    )
  await refresh(actorId)
}

const stageWrite = (action: string, id: string, key: string, fields: object = {}) =>
  productionWrite(`stage:${id}:${key}`, `/production/stages/${action}`, () => ({
    ...stageReference(id, key),
    ...fields,
  }))

export const productionActions = {
  assignStage: (id: string, key: string, assigneeId: string) =>
    stageWrite('assign', id, key, { assigneeId }),
  planStage: (
    id: string,
    key: string,
    patch: Pick<StagePlan, 'assigneeId' | 'equipmentId' | 'scheduledDate'>,
  ) => stageWrite('plan', id, key, patch),
  startStage: async (id: string, key: string) =>
    (await stageWrite('start', id, key)) ? null : 'Не вдалося почати роботу',
  pauseStage: (id: string, key: string, reason: string) => stageWrite('pause', id, key, { reason }),
  recordOutput: (
    id: string,
    key: string,
    entry: Pick<StageOutput, 'good' | 'scrap' | 'reason' | 'note'>,
    expectedVersion?: number,
  ) =>
    stageWrite('output', id, key, {
      ...entry,
      ...(expectedVersion ? { version: expectedVersion } : {}),
    }),
  finishStage: (id: string, key: string, expectedVersion?: number) =>
    stageWrite('finish', id, key, expectedVersion ? { version: expectedVersion } : {}),
  completeStage: (id: string, key: string) => stageWrite('complete', id, key),
  setProductionPriority: (id: string, priority: ProductionPriorityInput['priority']) =>
    productionWrite(
      `priority:${id}`,
      '/production/priority',
      () => {
        const order = production(id)
        return { id: order.id, version: versionOf(order), priority }
      },
      'PATCH',
    ),
  addPlanRow: (input: PlanRowCreate) =>
    productionWrite('plan-row:create', '/production/plan-rows', () => input),
  archivePlanRow: (id: string, expectedVersion?: number) =>
    productionWrite(
      `plan-row:${id}`,
      '/production/plan-rows/archive',
      () => ({
        id,
        version: expectedVersion ?? versionOf(proto.state.planRows.find((row) => row.id === id)),
      }),
      'PATCH',
    ),
}

export interface NestingPart {
  number: number
  name: string
  code: string | null
  planned: number
  cut: number
  sizeX: string
  sizeY: string
  areaM2: string
  weightKg: string
  cuttingSeconds: number
}
export interface NestingCandidate {
  productionOrderId: string
  number: string
  internal: boolean
  productName: string
  productCode: string | null
  deadline: string
  stageKey: string
  stageLabel: string
  version: number
  planned: string
  good: string
  remaining: string
}
export interface NestingPreview {
  fileName: string
  report: {
    jobName: string
    machine: string
    material: string
    thicknessMm: string
    wastePercent: string
    cuttingSeconds: number
    sheets: { sizeX: string; sizeY: string; weightKg: string; count: number }[]
    sheetCount: number
    parts: NestingPart[]
    plans: { name: string; repetitions: number; wastePercent: string }[]
    warnings: string[]
  }
  totals: { sheetWeightKg: string; wasteKg: string; cutPieces: number }
  candidates: NestingCandidate[]
  suggestions: {
    partNumber: number
    productionOrderId: string
    stageKey: string
    quantity: string
    by: 'code' | 'name'
  }[]
}
export interface NestingResult {
  id: string
  jobName: string
  baseQuantity: string
  stages: { number: string; stageLabel: string; quantity: string; done: boolean }[]
}

/** A repeated job is not a stale record, so its 409 text is shown as the server wrote it. */
const nestingRequest = async <T>(path: string, body: FormData): Promise<T> => {
  let response: Response
  try {
    response = await fetch(`/api${path}`, { method: 'POST', body, credentials: 'include' })
  } catch {
    throw new ApiError('Немає зв’язку із сервером. Спробуйте ще раз.', 0, 'NETWORK_ERROR')
  }
  const result = (await response.json().catch(() => null)) as {
    ok?: boolean
    data?: T
    error?: { message?: string; code?: string }
  } | null
  if (!response.ok || !result?.ok) {
    const message = result?.error?.message ?? 'Не вдалося прочитати карту розкрою'
    if (result?.error?.code === 'DUPLICATE_IMPORT') throw new Error(message)
    throw new ApiError(message, response.status, result?.error?.code ?? 'NESTING_ERROR')
  }
  return result.data as T
}

/** Reads the BySoft PDF on the server and proposes matches. Nothing is saved. */
export const previewNesting = (file: File) => {
  const body = new FormData()
  body.set('file', file, file.name)
  return nestingRequest<NestingPreview>('/production/nesting/preview', body)
}

export const confirmNesting = async (file: File, input: NestingConfirm) => {
  const body = new FormData()
  body.set('file', file, file.name)
  body.set('data', JSON.stringify(input))
  const actorId = session()?.actor.id
  const result = await nestingRequest<NestingResult>('/production/nesting', body)
  await refresh(actorId)
  return result
}
